import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, Loader2, MessageSquare, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/lib/hooks/useAuth';
import { env } from '@/config/env';

/**
 * LoginPage - Dedicated login page with centered Microsoft sign-in button.
 * Redirects to root (/) if user is already authenticated.
 */
function LoginPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, error, login } = useAuth();

  // Redirect to root if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Caricamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="w-full max-w-md p-8 bg-card rounded-lg shadow-lg border border-gray-200">
        {/* Logo/Icon */}
        <div className="flex justify-center mb-6">
          {env.appLogoUrl ? (
            <img
              src={env.appLogoUrl}
              alt={env.appName}
              className="h-16 w-auto object-contain"
            />
          ) : (
            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center shadow-md">
              <MessageSquare className="w-8 h-8 text-white" />
            </div>
          )}
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-center mb-2 text-foreground">
          {env.appName}
        </h1>
        <p className="text-center text-muted-foreground mb-6">
          Accedi per iniziare a chattare
        </p>

        {/* Sign in button */}
        <Button
          variant="primary"
          onClick={login}
          disabled={isLoading}
          className="w-full"
        >
          {isLoading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <LogIn className="mr-2 h-4 w-4" />
          )}
          Accedi con Microsoft
        </Button>

        {/* Error */}
        {error && (
          <Alert variant="danger" className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
}

export default LoginPage;
